import { white } from '@kmns-internal/furikake'
import { CheckCircleOutlineOutlined } from '@mui/icons-material'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined'
import ThumbUpAltOutlinedIcon from '@mui/icons-material/ThumbUpAltOutlined'
import {
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogTitle,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from '@mui/material'
import {
  ApprovalFlowStep,
  BulkApproveReports,
  PlaceNode,
  ReportListItem,
  ReportStatusEnum,
  User,
} from '@ulysses-inc/harami_api_client'
import { FC, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Link } from 'react-router-dom'
import date from 'src/exShared/util/date'
import { getRecorderName } from 'src/exShared/util/report/getRecorderName'
import { useApprovalReportsFilter } from 'src/features/approvals/hooks/useApprovalReportsFilter'
import { useApprovalReportsPagination } from 'src/features/approvals/hooks/useApprovalReportsPagination'
import { useLimitedFeatureAvailability } from 'src/features/featureAvailability/useLimitedFeatureAvailability'
import { GetApprovalReportsFilter } from 'src/state/ducks/approvals/types'
import { adjustCurrentPage } from 'src/util/adjustCurrentPage'
import Loading from '../../components/loading/Loading'
import approvalsOperations from '../../state/ducks/approvals/operations'
import { RootState } from '../../state/store'
import ApprovalReportsFilterDropDown from './filter/ApprovalReportsFilterDropDown'
import DateRangeFilter from './filter/DateRangeFilter'
import { useIsBulkApproving } from './useIsBulkApproving'

export const flattenNodes = (nodes: PlaceNode[]) => {
  const flatNodes: PlaceNode[] = []
  if (nodes && nodes.length > 0) {
    nodes.forEach(node => {
      flatNodes.push(node)
      if (node.nodes && node.nodes.length)
        flatNodes.push(...flattenNodes(node.nodes as PlaceNode[]))
    })
  }
  return flatNodes
}

const ApprovalReports: FC = () => {
  const dispatch = useDispatch()

  const { isFeatureAvailable } = useLimitedFeatureAvailability()

  const { isBulkApproving, setIsBulkApproving } = useIsBulkApproving()

  const {
    reports,
    count: reportsCount,
    isLoading: isReportsLoading,
  } = useSelector((state: RootState) => state.approvalsState.getApprovalReports)

  const [chooseReportUUIDs, setChooseReportUUIDs] = useState<{
    [key: string]: boolean
  }>({})
  const [isBulkApproveConfirmOpen, setIsBulkApproveConfirmOpen] =
    useState(false)

  const { apiFilter } = useApprovalReportsFilter()
  const { pagination, updatePagination } = useApprovalReportsPagination()

  useEffect(() => {
    approvalsOperations.getApprovalReports(dispatch, pagination, apiFilter)
  }, [apiFilter, pagination, dispatch])

  const handleCheckReport = (
    reportUUID: string | undefined,
    checked: boolean,
  ) => {
    if (!reportUUID) {
      return
    }
    if (checked) {
      // eslint-disable-next-line react-hooks/immutability -- FIXME: eslint-plugin-react-hooks が 7.0.1 にバージョンアップした際にエラーになるようになったため一旦無視。ここのコードを触る際に修正してください🙏
      chooseReportUUIDs[reportUUID] = true
      setChooseReportUUIDs({ ...chooseReportUUIDs })
    } else {
      delete chooseReportUUIDs[reportUUID]
      setChooseReportUUIDs({ ...chooseReportUUIDs })
    }
  }

  const getNextApprovers = (
    approvalStepCount: number,
    approvalFlowSteps: ApprovalFlowStep[],
    approvalStepApprovers: User[][],
  ): string => {
    if (approvalStepApprovers.length > 0) {
      return (
        approvalStepApprovers[approvalStepCount]
          ?.map(user => user.name)
          .join(',') ?? ''
      )
    }

    return (
      approvalFlowSteps[approvalStepCount]?.approvers
        ?.map(approver => approver.name)
        .join(',') ?? ''
    )
  }

  const bulkApproveReports = (reportUUIDs: string[]) => {
    const targetReports = reports
      .filter(
        (report: ReportListItem) =>
          report.uuid !== undefined &&
          reportUUIDs.includes(report.uuid) &&
          (report.approvals ?? []).length === 0 &&
          report.isInvalid !== 1 &&
          report.status?.status === ReportStatusEnum.APPROVAL_PENDING,
      )
      .map((report: ReportListItem) => ({
        reportUUID: report.uuid,
        approvalFlowId: (report.approvalFlows ?? [])[0]?.id,
      }))
    const stepIds = reports
      .filter(
        (report: ReportListItem) =>
          report.uuid !== undefined &&
          reportUUIDs.includes(report.uuid) &&
          report.approvals !== undefined &&
          (report.approvals ?? []).length !== 0 &&
          report.isInvalid !== 1 &&
          report.status?.status === ReportStatusEnum.APPROVAL_PENDING,
      )
      .map((report: ReportListItem) => {
        const approvalSteps =
          (report.approvals ?? [])[(report.approvals ?? []).length - 1]
            ?.steps ?? []
        const approvalStepCount = (approvalSteps ?? []).filter(
          step => step.stepApprovers && step.stepApprovers.length > 0,
        ).length
        return approvalSteps[approvalStepCount]?.id ?? 0
      })
      .filter((id: number) => id !== 0)
    const bulkApproveReportsPayload: BulkApproveReports = {
      reports: targetReports,
      stepIds,
      getApprovalReportsQuery: apiFilter,
    }
    approvalsOperations.bulkApproveReports(dispatch, {
      request: {
        bulkApproveReports: bulkApproveReportsPayload,
        ...pagination,
      },
      filter: apiFilter,
    })
  }

  const onBulkApproveButtonClicked = () => {
    setIsBulkApproveConfirmOpen(true)
  }

  const onBulkApproveConfirm = () => {
    setIsBulkApproveConfirmOpen(false)
    setIsBulkApproving(true)
    bulkApproveReports(Object.keys(chooseReportUUIDs))
    setChooseReportUUIDs({})
  }

  const isCheckedAll = () => {
    const reportsWithoutDeviation = reports.filter(
      (report: ReportListItem) => !report.isInvalid,
    )
    if (reportsWithoutDeviation.length === 0) return false
    const unChecked = reportsWithoutDeviation.filter(
      (report: ReportListItem) =>
        report.uuid !== undefined &&
        report.uuid !== '' &&
        !Object.prototype.hasOwnProperty.call(chooseReportUUIDs, report.uuid),
    )
    return unChecked.length === 0
  }

  const handleCheckAll = (checked: boolean) => {
    const reportsWithoutDeviation = reports.filter(
      (report: ReportListItem) => !report.isInvalid,
    )
    if (checked) {
      reportsWithoutDeviation.forEach((report: ReportListItem) => {
        if (report.uuid === undefined || report.uuid === '') {
          return
        }
        chooseReportUUIDs[report.uuid] = true
      })
      setChooseReportUUIDs({ ...chooseReportUUIDs })
      return
    }
    reportsWithoutDeviation.forEach((report: ReportListItem) => {
      if (report.uuid === undefined || report.uuid === '') {
        return
      }
      delete chooseReportUUIDs[report.uuid]
    })
    setChooseReportUUIDs({ ...chooseReportUUIDs })
  }

  // 現在のページを調整する
  const currentPage = adjustCurrentPage(
    pagination.limit,
    pagination.offset,
    reportsCount,
  )

  const onPageChange = (
    pageNum: number,
    pageSize?: number,
    apiFilter?: GetApprovalReportsFilter,
  ) => {
    if (pageSize) {
      approvalsOperations.changeApprovalReportsPage(
        dispatch,
        pageNum,
        pageSize,
        apiFilter,
      )
    }
  }

  const onPageSizeChange = (
    size: number,
    apiFilter?: GetApprovalReportsFilter,
  ) => {
    updatePagination(size, 0)
    approvalsOperations.changeApprovalReportsSize(dispatch, size, apiFilter)
  }

  const renderReports = (reports: ReportListItem[]) => {
    return reports.map((report, index) => {
      const approvalFlowSteps =
        report.approvalFlows &&
        report.approvalFlows.length > 0 &&
        report.approvalFlows[0]?.steps
          ? report.approvalFlows[0].steps
          : []
      const approvalSteps =
        report.approvals &&
        report.approvals.length > 0 &&
        report.approvals[report.approvals.length - 1]?.steps
          ? report.approvals[report.approvals.length - 1]?.steps
          : []
      const approvalStepApprovers: User[][] =
        approvalSteps && approvalSteps.length > 0
          ? approvalSteps.map(step => (step.approvers ? step.approvers : []))
          : []
      const approvalStepCount = (approvalSteps ?? []).filter(
        step => step.stepApprovers && step.stepApprovers.length > 0,
      ).length
      return (
        <TableRow
          key={`sectionDate_${index}`}
          hover
          component={Link}
          to={`/reports/${report.uuid}?templateId=${report.templateId}`}
          sx={{ textDecoration: 'none' }}
        >
          <TableCell
            padding="checkbox"
            size="small"
            onClick={e => e.stopPropagation()}
          >
            {!report.isInvalid && (
              <Checkbox
                checked={
                  report.uuid !== undefined && report.uuid !== ''
                    ? Object.prototype.hasOwnProperty.call(
                        chooseReportUUIDs,
                        report.uuid,
                      )
                    : false
                }
                onChange={e => handleCheckReport(report.uuid, e.target.checked)}
                size="small"
              />
            )}
          </TableCell>
          <TableCell size="small" sx={{ wordBreak: 'break-all' }}>
            {report.name}
          </TableCell>
          <TableCell size="small" sx={{ width: '10%' }}>
            {date.formatYYYYMMDD(report.reportDate)}
          </TableCell>
          <TableCell size="small" sx={{ width: '10%' }}>
            {report.place?.name}
          </TableCell>
          <TableCell size="small" sx={{ width: '8%' }}>
            {!!report.isInvalid && (
              <ErrorOutlineIcon sx={{ color: 'error.main' }} />
            )}
          </TableCell>
          <TableCell size="small" sx={{ width: '15%' }}>
            {getRecorderName(report)}
          </TableCell>
          <TableCell size="small" sx={{ width: '20%' }}>
            {approvalFlowSteps.length === approvalStepCount ? (
              <Stack direction="row" alignItems="center" spacing={1}>
                <CheckCircleOutlineOutlined sx={{ color: 'success.main' }} />
                <Typography sx={{ color: 'success.main' }}>完了</Typography>
              </Stack>
            ) : (
              <Stack direction="row" alignItems="center" spacing={1}>
                {getNextApprovers(
                  approvalStepCount,
                  approvalFlowSteps,
                  approvalStepApprovers,
                )}
              </Stack>
            )}
          </TableCell>
          <TableCell size="small" sx={{ width: '5%' }}>
            {`${approvalStepCount}/${approvalFlowSteps.length}`}
          </TableCell>
        </TableRow>
      )
    })
  }

  return (
    <Stack sx={{ flexGrow: 1, minHeight: 0 }}>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ m: 2 }}
      >
        <Stack direction="row" spacing={1}>
          <ApprovalReportsFilterDropDown />
          <DateRangeFilter />
        </Stack>
        <Stack direction="row" alignItems="center" spacing={2}>
          {isFeatureAvailable('approvalFlows') && (
            <Link to="/approvalFlows" style={{ textDecoration: 'none' }}>
              <Button variant="outlined" startIcon={<SettingsOutlinedIcon />}>
                承認フロー設定
              </Button>
            </Link>
          )}
          <Button
            variant="contained"
            disabled={
              isBulkApproving || Object.keys(chooseReportUUIDs).length === 0
            }
            onClick={onBulkApproveButtonClicked}
            startIcon={
              isBulkApproving ? (
                <CircularProgress size={14} color="inherit" />
              ) : (
                <ThumbUpAltOutlinedIcon />
              )
            }
          >
            {isBulkApproving ? '一括承認中…' : '一括承認'}
          </Button>
        </Stack>
      </Stack>
      <TableContainer
        sx={{
          overflow: 'auto',
          position: 'relative',
          flexGrow: 1,
          minHeight: 0,
          bgcolor: 'white',
        }}
      >
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell
                padding="checkbox"
                sx={{ backgroundColor: white }}
                size="small"
              >
                <Checkbox
                  checked={isCheckedAll()}
                  onChange={e => handleCheckAll(e.target.checked)}
                  size="small"
                />
              </TableCell>
              <TableCell
                sx={{ backgroundColor: white, wordBreak: 'break-all' }}
                size="small"
              >
                レポート名
              </TableCell>
              <TableCell
                sx={{ backgroundColor: white, width: '10%' }}
                size="small"
              >
                実施日
              </TableCell>
              <TableCell
                sx={{ backgroundColor: white, width: '10%' }}
                size="small"
              >
                現場名
              </TableCell>
              <TableCell
                sx={{ backgroundColor: white, width: '12%' }}
                size="small"
              >
                逸脱有無
              </TableCell>
              <TableCell
                sx={{ backgroundColor: white, width: '16%' }}
                size="small"
              >
                依頼者
              </TableCell>
              <TableCell
                sx={{ backgroundColor: white, width: '16%' }}
                size="small"
              >
                承認者
              </TableCell>
              <TableCell
                sx={{ backgroundColor: white, width: '8%' }}
                size="small"
              >
                進捗
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody sx={{ backgroundColor: white }}>
            {isReportsLoading ? (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  <Loading />
                </TableCell>
              </TableRow>
            ) : (
              renderReports(reports)
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        component="div"
        count={reportsCount}
        page={currentPage - 1}
        rowsPerPage={pagination.limit ?? 25}
        rowsPerPageOptions={[25, 50, 100]}
        labelRowsPerPage="1ページあたり:"
        labelDisplayedRows={({ from, to, count }) =>
          `${from}〜${to} (${count !== -1 ? count : '不明'} 件中)`
        }
        onPageChange={(_, newPage) =>
          onPageChange(newPage + 1, pagination.limit, apiFilter)
        }
        onRowsPerPageChange={e =>
          onPageSizeChange(parseInt(e.target.value, 10), apiFilter)
        }
        sx={{
          flexShrink: 0,
          '& .MuiTablePagination-selectLabel': {
            margin: 0,
          },
          '& .MuiTablePagination-displayedRows': {
            margin: 0,
          },
        }}
      />
      <Dialog
        open={isBulkApproveConfirmOpen}
        onClose={() => setIsBulkApproveConfirmOpen(false)}
      >
        <DialogTitle>
          逸脱の存在しないレポートの一括承認を実行しますがよろしいですか？
        </DialogTitle>
        <DialogActions>
          <Button onClick={() => setIsBulkApproveConfirmOpen(false)}>
            キャンセル
          </Button>
          <Button variant="contained" onClick={onBulkApproveConfirm}>
            実行
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  )
}

export default ApprovalReports
